// System
import { useMemo } from 'react';
import { Box } from '@mui/material';
import { parseISO } from 'date-fns';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

// Components
import { DateLabel } from '@Components/shared/labels/DateLabel';
import { useEllipsis } from '@Components/shared/styles/useEllipsis';
import {
  EnumTableColumnHeader,
  IntegerTableColumnHeader,
  StringTableColumnHeader,
  TableColumnHeader,
} from '@Components/shared/tables/columns';

// Contracts
import { Filterable } from '@contracts/common/traits/Filterable';
import {
  TableColumnDescription,
  TableColumnDescriptionArray,
} from '@contracts/view/TableColumnDescription';
import { ConfigItemDto } from '@contracts/api/dto/providers/ConfigDto';

// Utils
import {
  ConfigListColumns,
  ConfigListFilter,
  configListColumns,
} from '@utils/tables/configs/configListFilter';

// Features
import TruncateText from '@Components/truncate-text/TruncateText';

// Redux
import { useConfigsTable } from '@Modal/hooks/providers/useConfigsTable';
import { useUpdateProviderConfig } from '@Modal/hooks/providers/useUpdateProviderConfig';
import { CurrencyInterface } from '@Modal/reducers/providers/types';

type HeaderOptions = {
  filter: Filterable<ConfigListFilter>;
};

type Columns = TableColumnDescriptionArray<ConfigItemDto, HeaderOptions, typeof configListColumns>;

type Column = TableColumnDescription<ConfigItemDto, HeaderOptions, ConfigListColumns>;

const useSx = () => {
  return {
    id: { width: '80px' },
  } satisfies Record<string, SystemStyleObject<Theme>>;
};

function formatNumber(value: number): string {
  return value.toLocaleString();
}

export const useConfigsTableColumns = () => {
  const ellipsis = useEllipsis();
  const styles = useSx();
  const { handleFilterSelect } = useConfigsTable();
  const { handleShowDeleteConfigModal } = useUpdateProviderConfig();
  return useMemo(
    (): Column[] =>
      [
        {
          name: 'config.delete',
          title: 'Configs',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box
              sx={{ bgcolor: '#FF0000', color: 'white', textAlign: 'center', borderRadius: '8px' }}
              onClick={(event) => {
                event.stopPropagation();
                handleShowDeleteConfigModal(row as CurrencyInterface);
              }}
            >
              <strong>Delete</strong>
            </Box>
          ),
        },
        {
          name: 'config.currency',
          title: 'Currency',
          sx: { minWidth: '80px', maxWidth: '180px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'currency'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.currency}</Box>,
        },
        {
          name: 'config.id',
          title: 'ID',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.id && (
                <TruncateText text={row?.id || 'No Info'} maxLength={8} textStyles={styles.id} />
              )}
            </Box>
          ),
        },
        {
          name: 'config.providerId',
          title: 'Casino ID',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.providerId && (
                <TruncateText
                  text={row?.providerId || 'No Info'}
                  maxLength={8}
                  textStyles={styles.id}
                />
              )}
            </Box>
          ),
        },
        {
          name: 'config.createdAt',
          title: 'Created At',
          sx: { minWidth: '230px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.createdAt && (
                <DateLabel date={new Date(parseISO(row?.createdAt))} format="long" />
              )}
            </Box>
          ),
        },
        {
          name: 'config.updatedAt',
          title: 'Updated At',
          sx: { minWidth: '170px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.updatedAt && (
                <DateLabel date={new Date(parseISO(row?.updatedAt))} format="long" />
              )}
            </Box>
          ),
        },
        {
          name: 'config.betSuggestions',
          title: 'Bet Suggestions',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{row?.betSuggestions.toString() || 'No Info'}</Box>
          ),
        },
        {
          name: 'config.maxWin',
          title: 'Max Win',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => (
            <IntegerTableColumnHeader
              title={title}
              codeName={'maxWin'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{formatNumber(row?.maxWin || 0)}</Box>,
        },
        {
          name: 'config.defaultBet',
          title: 'Default Bet',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{formatNumber(row?.defaultBet || 0)}</Box>,
        },
        {
          name: 'config.minBet',
          title: 'Min Bet',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'minBet'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{formatNumber(row?.minBet || 0)}</Box>,
        },
        {
          name: 'config.maxBet',
          title: 'Max Bet',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'maxBet'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{formatNumber(row?.maxBet || 0)}</Box>,
        },
        {
          name: 'config.defaultAutoCashout',
          title: 'Default Auto Cashout',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{formatNumber(row?.defaultAutoCashout || 0)}</Box>
          ),
        },
        {
          name: 'config.minAutoCashout',
          title: 'Min Auto Cashout',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{formatNumber(row?.minAutoCashout || 0)}</Box>
          ),
        },
        {
          name: 'config.maxAutoCashout',
          title: 'Max Auto Cashout',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{formatNumber(row?.maxAutoCashout || 0)}</Box>
          ),
        },
        {
          name: 'config.isRoundable',
          title: 'Is Roundable',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => (
            <EnumTableColumnHeader
              title={title}
              codeName="isRoundable"
              options={[
                { key: 'True', value: 'true' },
                { key: 'False', value: 'false' },
              ]}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.isRoundable ? 'True' : 'False'}</Box>,
        },
        {
          name: 'config.allowedBetType',
          title: 'Allowed Bet Type',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.allowedBetType || 'No Info'}</Box>,
        },
      ] satisfies Columns,
    [ellipsis]
  );
};
